import FuturesConfigurationFields from '@/features/strategyBacktests/FuturesConfigurationFields';
import { emptyFuturesConfiguration, parseFuturesConfiguration } from '@/features/strategyBacktests/futuresConfigurationDraft';
import React, { useState, useEffect, useMemo, useCallback, useDeferredValue, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaChartBar, FaCheckSquare, FaClone, FaPlay, FaPlus } from 'react-icons/fa';
import UserLayout from '@/components/Layout/UserLayout';
import { showToast } from '@components/Layout/Toast';
import Button from '@components/UI/Button';
import Alert from '@components/UI/Alert';
import {
    ExitCandidateSelect,
    FormCheckbox,
    DeleteButton,
    Card,
    ConfirmationDialog,
    OptionBuyingStrategySelect,
    TradingStrategySelect
} from '@components/UI';
import DataTable from '@components/UI/DataTable';
import Modal from '@components/UI/Modal';
import FormInput from '@components/UI/FormInput';
import FormNumberInput from '@components/UI/FormNumberInput';
import FormDateInput from '@components/UI/FormDateInput';
import FormTimeInput from '@components/UI/FormTimeInput';
import FormSelect from '@components/UI/FormSelect';
import TradingProfileSelect from '@/components/UI/TradingProfileSelect';
import PortfolioSelect from '@/components/UI/PortfolioSelect';
import { showSpinner, hideSpinner } from '@components/Layout/LoadingSpinner';
import TradingProfileDisplay from '@components/Settings/TradingProfileDisplay';
import './PortfolioBacktest.tailwind.css';
import { capitalizeFirstLetter, getDate, calculatePercentageChangeFast } from '@einhaus/meteoric-helpers';
import { DEFAULT_PORTFOLIO_BALANCE, getLatestSafeMarketDateTime, getResearchWindow } from '@einhaus/project314-shared';
import { OPTIONS_DATA_START_DATETIME } from '@/config/constants';
import type { ColumnDef, Row } from '@/components/UI/dataTableConfig';
import type {
    BestBacktestArtifactUniverseMode,
    DirectionMaxRule,
    PortfolioBacktest as PortfolioBacktestType,
    PositionSizeRule,
    TradingProfile,
    TradingStrategy
} from '@einhaus/project314-types';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner.tsx';
import { trpc } from '@/api/trpc.ts';
import { useQueryClient } from '@tanstack/react-query';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import { useAppQuery } from '@/hooks/useAppQuery';
import { usePortfolios } from '@/hooks/usePortfolios';
import { useTradingProfiles } from '@/hooks/useTradingProfiles';
import { useTradingRequisites } from '@/hooks/useTradingRequisites';
import PortfolioBacktestColumnVisibilityControl from './PortfolioBacktestColumnVisibilityControl';
import PortfolioBacktestEvidenceModeControl from './PortfolioBacktestEvidenceModeControl';
import PortfolioBacktestWalkForwardCell from './PortfolioBacktestWalkForwardCell';
import PortfolioBacktestCapitalControls, { StrategyPositionSizeRules } from './PortfolioBacktestCapitalControls';
import { FrozenRunConfiguration } from './PortfolioBacktestFrozenCapital';
import PortfolioBacktestIdentityCell from './PortfolioBacktestIdentityCell';
import PortfolioBacktestRefreshControl from './PortfolioBacktestRefreshControl';
import PortfolioBacktestRefreshSummary from './PortfolioBacktestRefreshSummary';
import {
    indexPortfolioRefreshStatuses,
    resolvePortfolioRefreshStatus,
    matchesPortfolioRefreshFilter,
    type PortfolioRefreshFilter
} from './portfolioBacktestRefreshStatus';
import {
    buildFrozenSnapshotSearchTokens,
    compareFrozenSnapshotToLive,
    summarizeFrozenCapital,
    UNKNOWN_FROZEN_SNAPSHOT_DRIFT,
    type FrozenSnapshotDrift
} from './portfolioBacktestFrozenSnapshot';
import { formatBacktestPercent } from './portfolioBacktestNumberFormatters';
import { calculatePortfolioCagr } from '@/utils/backtestHelpers';
import { buildPortfolioBacktestDuplicateFormState, type PortfolioBacktestFormStrategySetting } from './portfolioBacktestDuplicate';
import { findUniformColumnIds } from './portfolioBacktestUniformColumns';
import { useFrozenExitCandidateLabels } from './useFrozenExitCandidateLabels';
import { profileUsesCohortBestBacktestSelection } from './portfolioBacktestEvidenceMode';
import {
    getDefaultPortfolioBacktestColumns,
    getTimingEdgeTooltip,
    loadPortfolioBacktestVisibleColumns,
    portfolioBacktestColumnPresets,
    portfolioBacktestColumnRegistry,
    PORTFOLIO_BACKTEST_VISIBLE_COLUMNS_STORAGE_KEY,
    TIMING_EDGE_DISCLAIMER,
    WALK_FORWARD_DISCLAIMER,
    type PortfolioBacktestColumnId
} from './portfolioBacktestColumnConfig';

const getTableColumnId = (column: ColumnDef<PortfolioBacktestType>): string | null => {
    const candidate = column as { accessorKey?: string | number; id?: string };

    if (typeof candidate.id === 'string') {
        return candidate.id;
    }

    if (typeof candidate.accessorKey === 'string') {
        return candidate.accessorKey;
    }

    return null;
};

// A running or queued backtest means results are still changing, so the list should poll for fresh data.
const hasRunningBacktest = (backtests: readonly { status: string }[] | undefined): boolean =>
    backtests?.some((backtest) => backtest.status === 'running' || backtest.status === 'queued') ?? false;

const strategyUsesOptions = (strategy: Pick<TradingStrategy, 'legs'>): boolean =>
    strategy.legs.some((leg) => leg.derivativeType === 'Option');

// Names are free text: the table's default sort would parse the digits out of "S461 ext0.65 …" and compare them as a number.
const compareBacktestNames = (rowA: Row<PortfolioBacktestType>, rowB: Row<PortfolioBacktestType>): number =>
    rowA.original.name.localeCompare(rowB.original.name);

const PortfolioBacktestComponent: React.FC = () => {
    const { id } = useParams<{ id?: string }>();
    const portfolioId = id ? parseInt(id, 10) : undefined;
    const queryClient = useQueryClient();
    const refreshSchedulesQuery = useAppQuery(
        {
            ...trpc.user.fetchPortfolioBacktestRefreshSchedules.queryOptions({ portfolioId }),
            refetchInterval: 60000
        },
        { fallbackErrorMessage: 'Failed to load refresh schedules', showErrorToast: true }
    );
    const refreshStatusByBaseline = useMemo(
        () => indexPortfolioRefreshStatuses(refreshSchedulesQuery.data?.schedules ?? []),
        [refreshSchedulesQuery.data]
    );
    const refreshSchedulesChanged = useCallback(() => {
        void queryClient.invalidateQueries({ queryKey: trpc.user.fetchPortfolioBacktestRefreshSchedules.queryKey() });
        void queryClient.invalidateQueries({ queryKey: trpc.user.fetchPortfolioBacktests.queryKey() });
    }, [queryClient]);
    type BacktestStatusFilter = 'all' | 'capitalStarved' | PortfolioBacktestType['status'];

    const [error, setError] = useState<string | null>(null);
    const [showNewBacktestModal, setShowNewBacktestModal] = useState<boolean>(false);
    const [duplicateSourceBacktest, setDuplicateSourceBacktest] = useState<PortfolioBacktestType | null>(null);
    const [showSettingsModal, setShowSettingsModal] = useState<boolean>(false);
    const [currentSettings, setCurrentSettings] = useState<TradingProfile | null>(null);
    const [selectedRows, setSelectedRows] = useState<Record<string, boolean>>({});

    const [visibleColumns, setVisibleColumns] = useState<Set<PortfolioBacktestColumnId>>(() => {
        try {
            return loadPortfolioBacktestVisibleColumns(localStorage);
        } catch {
            // Storage access itself can throw (privacy modes, blocked site data); fall back to the defaults.
            return getDefaultPortfolioBacktestColumns();
        }
    });

    const [searchQuery, setSearchQuery] = useState<string>('');
    const [refreshFilter, setRefreshFilter] = useState<PortfolioRefreshFilter>('all');
    const [selectedStatusFilter, setSelectedStatusFilter] = useState<BacktestStatusFilter>('all');
    const [selectedStrategyFilter, setSelectedStrategyFilter] = useState<string>('all');
    const [selectedPortfolioFilter, setSelectedPortfolioFilter] = useState<number | null>(null);
    const [pendingDeleteIds, setPendingDeleteIds] = useState<number[]>([]);
    const [pendingDeleteLabel, setPendingDeleteLabel] = useState<string>('');
    const deferredSearchQuery = useDeferredValue(searchQuery);

    // Fetch portfolio backtests with tRPC
    const backtestsQuery = useAppQuery(
        {
            ...trpc.user.fetchPortfolioBacktests.queryOptions({ portfolioId: portfolioId || 0 }),
            // Poll every 15s (and keep data always stale) while any backtest is running or queued
            staleTime: (query) => (hasRunningBacktest(query.state.data?.backtests) ? 0 : 60000),
            refetchOnWindowFocus: (query) => hasRunningBacktest(query.state.data?.backtests),
            refetchInterval: (query) => (hasRunningBacktest(query.state.data?.backtests) ? 15000 : false)
        },
        {
            fallbackErrorMessage: 'Failed to load backtests',
            showErrorToast: true,
            setError: (msg) => setError(msg)
        }
    );

    // Fetch portfolio data with tRPC
    const portfolioQuery = useAppQuery(
        {
            ...trpc.user.fetchPortfolio.queryOptions({
                id: portfolioId || 0,
                shouldFetchTrades: true
            }),
            enabled: !!portfolioId
        },
        {
            fallbackErrorMessage: 'Failed to load portfolio data',
            showErrorToast: true,
            setError: (msg) => setError(msg)
        }
    );

    // Fetch trading requisites with tRPC
    const { requisites, isLoading: requisitesLoading } = useTradingRequisites({
        errorConfig: {
            fallbackErrorMessage: 'Failed to load trading requisites',
            showErrorToast: true,
            setError: (msg) => setError(msg)
        }
    });

    // Fetch trading profiles with tRPC
    const { isLoading: tradingProfilesLoading, tradingProfiles } = useTradingProfiles({
        enabled: showNewBacktestModal,
        errorConfig: {
            fallbackErrorMessage: 'Failed to load trading profiles',
            showErrorToast: true,
            setError: (msg) => setError(msg)
        }
    });

    // Fetch all user portfolios for selection dropdown
    const { portfolios: allPortfolios, isLoading: allPortfoliosLoading } = usePortfolios({
        enabled: !portfolioId,
        errorConfig: {
            fallbackErrorMessage: 'Failed to load portfolios',
            showErrorToast: true,
            setError: (msg) => setError(msg)
        }
    });

    // Extract data from queries
    const backtests = useMemo(() => {
        const data = backtestsQuery.data?.backtests || [];
        // Sort by ID in descending order (newest first)
        return [...data].sort((a, b) => b.id - a.id);
    }, [backtestsQuery.data?.backtests]);

    const portfolio = portfolioQuery.data?.portfolios?.[0] || null;
    const portfolioMap = useMemo(() => new Map(allPortfolios.map((item) => [item.id, item])), [allPortfolios]);

    const strategyNameMap = useMemo(
        () => new Map((requisites?.tradingStrategies || []).map((strategy) => [strategy.id, strategy.name])),
        [requisites?.tradingStrategies]
    );

    /** OBS names ride along on the requisites fetch, so the frozen cells can label ids at no extra cost. */
    const optionBuyingStrategyNameMap = useMemo(() => {
        const nameMap = new Map<number, string>();

        for (const optionBuyingStrategy of requisites?.optionBuyingStrategies ?? []) {
            if (optionBuyingStrategy.name) nameMap.set(optionBuyingStrategy.id, optionBuyingStrategy.name);
        }

        return nameMap;
    }, [requisites?.optionBuyingStrategies]);

    const exitCandidateLabelMap = useFrozenExitCandidateLabels(backtests);

    /**
     * Live portfolio state to compare each run's frozen snapshot against. Exactly one of the two
     * queries runs per route: the single-portfolio view fetches one portfolio, the all-portfolios
     * view fetches the list.
     */
    const livePortfolioMap = useMemo(() => (portfolio ? new Map([[portfolio.id, portfolio]]) : portfolioMap), [portfolio, portfolioMap]);

    const frozenDriftByBacktestId = useMemo(() => {
        const driftMap = new Map<number, FrozenSnapshotDrift>();

        backtests.forEach((backtest) => {
            driftMap.set(backtest.id, compareFrozenSnapshotToLive(backtest, livePortfolioMap.get(backtest.portfolioId), strategyNameMap));
        });

        return driftMap;
    }, [backtests, livePortfolioMap, strategyNameMap]);

    const getFrozenDrift = useCallback(
        (backtestId: number): FrozenSnapshotDrift => frozenDriftByBacktestId.get(backtestId) ?? UNKNOWN_FROZEN_SNAPSHOT_DRIFT,
        [frozenDriftByBacktestId]
    );

    // Show loading spinner when component mounts
    useEffect(() => {
        showSpinner();

        // Hide spinner when component unmounts
        return () => {
            hideSpinner();
        };
    }, []);

    useEffect(() => {
        try {
            localStorage.setItem(PORTFOLIO_BACKTEST_VISIBLE_COLUMNS_STORAGE_KEY, JSON.stringify([...visibleColumns]));
        } catch {
            // Ignore persistence errors and keep the in-memory settings active.
        }
    }, [visibleColumns]);

    // New backtest form state
    const [backtestName, setBacktestName] = useState<string>('');
    // startDate/endDate hold canonical 'YYYY-MM-DD HH:mm:ss' Eastern wall-clock strings end-to-end (defaults,
    // presets, inputs, submit). Never route them through Date — a UTC round-trip shifts evening ET times to
    // the next calendar day.
    const [startDate, setStartDate] = useState<string>(OPTIONS_DATA_START_DATETIME);
    // Validation surface: default to the latest safe (settled) market date, past the development cutoff.
    const [endDate, setEndDate] = useState<string>(getLatestSafeMarketDateTime());
    const [trainingEndDate, setTrainingEndDate] = useState(
        () => getResearchWindow(OPTIONS_DATA_START_DATETIME, new Date()).walkForwardStart
    );
    const [startingBalance, setStartingBalance] = useState<number | null>(DEFAULT_PORTFOLIO_BALANCE);
    const [taxesEnabled, setTaxesEnabled] = useState(false);
    const [federalTaxRate, setFederalTaxRate] = useState<number | null>(0);
    const [stateTaxRate, setStateTaxRate] = useState<number | null>(0);
    const [shouldLog, setShouldLog] = useState<boolean>(false);
    const [orchestratorPriority, setOrchestratorPriority] = useState<number>(8);
    const [bypassCache, setBypassCache] = useState<boolean>(false);
    const [backtestSelectionArtifactUniverseMode, setBacktestSelectionArtifactUniverseMode] =
        useState<BestBacktestArtifactUniverseMode>('strict_operational');
    const [hasAcknowledgedBacktestReconstruction, setHasAcknowledgedBacktestReconstruction] = useState(false);
    const [selectedPortfolioId, setSelectedPortfolioId] = useState<number | undefined>(portfolioId);
    const modalPortfolio = selectedPortfolioId ? allPortfolios.find((p) => p.id === selectedPortfolioId) || portfolio : portfolio;
    const [backtestDirectionMaxRules, setBacktestDirectionMaxRules] = useState<DirectionMaxRule[]>([]);
    const [backtestPositionSizeRules, setBacktestPositionSizeRules] = useState<PositionSizeRule[]>([]);

    const [strategySettings, setStrategySettings] = useState<Record<number, PortfolioBacktestFormStrategySetting>>({});
    const initializedBacktestForm = useRef<{ portfolioId: number; duplicateBacktestId: number | null } | null>(null);
    const isStartingBacktest = useRef(false);

    const [includedStrategyIds, setIncludedStrategyIds] = useState<Set<number>>(new Set());
    const tradingProfileById = useMemo(
        () => new Map(tradingProfiles.map((tradingProfile) => [tradingProfile.id, tradingProfile])),
        [tradingProfiles]
    );
    const cohortSelectionStrategyNames = useMemo(
        () =>
            (modalPortfolio?.strategiesSubscribed ?? []).flatMap((strategy) => {
                if (!includedStrategyIds.has(strategy.id)) return [];

                const tradingProfileId = strategySettings[strategy.id]?.settingsId;
                const tradingProfile = tradingProfileId ? tradingProfileById.get(tradingProfileId) : undefined;

                return profileUsesCohortBestBacktestSelection(tradingProfile) ? [strategy.name] : [];
            }),
        [includedStrategyIds, modalPortfolio?.strategiesSubscribed, strategySettings, tradingProfileById]
    );
    const usesCohortBestBacktestSelection = cohortSelectionStrategyNames.length > 0;
    const needsBacktestReconstructionAcknowledgement =
        usesCohortBestBacktestSelection &&
        backtestSelectionArtifactUniverseMode === 'trade_cutoff_reconstruction' &&
        !hasAcknowledgedBacktestReconstruction;

    useEffect(() => {
        if (usesCohortBestBacktestSelection) return;

        setBacktestSelectionArtifactUniverseMode('strict_operational');
        setHasAcknowledgedBacktestReconstruction(false);
    }, [usesCohortBestBacktestSelection]);

    const portfolioBacktestStrategySelections = useMemo(
        () =>
            Array.from(includedStrategyIds).flatMap((strategyId) => {
                const settings = strategySettings[strategyId];

                if (!settings?.settingsId) return [];

                return [
                    {
                        strategyId,
                        tradingProfileId: settings.settingsId,
                        optionBuyingStrategyIds: settings.optionBuyingStrategyIds,
                        exitCandidateIds: settings.exitCandidateIds,
                        ...(settings.positionSizeRules?.length ? { positionSizeRules: settings.positionSizeRules } : {})
                    }
                ];
            }),
        [includedStrategyIds, strategySettings]
    );
    const portfolioBacktestPreflightSelections = useMemo(
        () =>
            portfolioBacktestStrategySelections.map(({ strategyId, tradingProfileId, optionBuyingStrategyIds, exitCandidateIds }) => ({
                strategyId,
                tradingProfileId,
                optionBuyingStrategyIds,
                exitCandidateIds
            })),
        [portfolioBacktestStrategySelections]
    );
    const canPreflightPortfolioBacktest =
        showNewBacktestModal &&
        Boolean(selectedPortfolioId || portfolioId) &&
        includedStrategyIds.size > 0 &&
        portfolioBacktestStrategySelections.length === includedStrategyIds.size;
    const exitCandidatePreflightQuery = useAppQuery(
        {
            ...trpc.user.preflightPortfolioBacktestExitCandidates.queryOptions({
                portfolioId: selectedPortfolioId || portfolioId || 0,
                startDateTime: startDate,
                endDateTime: endDate,
                tradingProfilesForStrategies: portfolioBacktestPreflightSelections
            }),
            enabled: canPreflightPortfolioBacktest,
            staleTime: 15000
        },
        {
            fallbackErrorMessage: 'Failed to validate portfolio backtest selections',
            showErrorToast: false
        }
    );
    const exitCandidatePreflightByStrategy = useMemo(
        () => new Map((exitCandidatePreflightQuery.data?.strategies ?? []).map((strategy) => [strategy.strategyId, strategy])),
        [exitCandidatePreflightQuery.data?.strategies]
    );
    const modalStrategyById = useMemo(
        () => new Map((modalPortfolio?.strategiesSubscribed ?? []).map((strategy) => [strategy.id, strategy])),
        [modalPortfolio?.strategiesSubscribed]
    );
    const duplicateFormState = useMemo(() => {
        if (!duplicateSourceBacktest || !modalPortfolio || duplicateSourceBacktest.portfolioId !== modalPortfolio.id) {
            return null;
        }

        return buildPortfolioBacktestDuplicateFormState(duplicateSourceBacktest, modalPortfolio.strategiesSubscribed ?? []);
    }, [duplicateSourceBacktest, modalPortfolio]);
    const duplicateStrategyIdsMissingSettings =
        duplicateFormState?.includedStrategyIds.filter(
            (strategyId) => includedStrategyIds.has(strategyId) && !strategySettings[strategyId]?.settingsId
        ) ?? [];
    const duplicateProfileChangeMessages = useMemo(
        () =>
            (duplicateFormState?.profileChanges ?? []).map((profileChange) => {
                const strategyName = modalStrategyById.get(profileChange.strategyId)?.name ?? strategyNameMap.get(profileChange.strategyId);
                const currentProfileName = tradingProfileById.get(profileChange.currentTradingProfileId)?.name;
                const strategyLabel = strategyName
                    ? `${strategyName} (#${profileChange.strategyId})`
                    : `Strategy #${profileChange.strategyId}`;
                const currentProfileLabel = currentProfileName
                    ? `${currentProfileName} (#${profileChange.currentTradingProfileId})`
                    : `Profile #${profileChange.currentTradingProfileId}`;
                const originalProfileLabel = profileChange.originalTradingProfileName
                    ? `${profileChange.originalTradingProfileName} (#${profileChange.originalTradingProfileId})`
                    : `Profile #${profileChange.originalTradingProfileId}`;

                return {
                    message: `${strategyLabel}: using ${currentProfileLabel} instead of ${originalProfileLabel}.`,
                    strategyId: profileChange.strategyId
                };
            }),
        [duplicateFormState?.profileChanges, modalStrategyById, strategyNameMap, tradingProfileById]
    );
    const hasBlockingExitCandidateCoverage = useMemo(
        () =>
            (exitCandidatePreflightQuery.data?.strategies ?? []).some((strategy) => {
                const modalStrategy = modalStrategyById.get(strategy.strategyId);
                const isOptionsStrategy = strategy.isOptionsStrategy ?? (modalStrategy ? strategyUsesOptions(modalStrategy) : false);

                return (
                    (isOptionsStrategy && (strategy.selectedOptionBuyingStrategyIds?.length ?? 0) === 0) ||
                    strategy.selectedExitCandidateIds.length === 0 ||
                    strategy.coverage.errorMessages.length > 0 ||
                    strategy.coverage.failedBacktests > 0 ||
                    (strategy.coverage.requestStatus !== null &&
                        ['failed', 'cancelled', 'stalled'].includes(strategy.coverage.requestStatus))
                );
            }),
        [exitCandidatePreflightQuery.data?.strategies, modalStrategyById]
    );
    const hasPortfolioBacktestPreflightError =
        canPreflightPortfolioBacktest &&
        (exitCandidatePreflightQuery.isLoading ||
            exitCandidatePreflightQuery.isError ||
            !exitCandidatePreflightQuery.data ||
            exitCandidatePreflightQuery.data.errorMessages.length > 0 ||
            hasBlockingExitCandidateCoverage);

    // Refetch backtests
    const refetchBacktests = useCallback(async () => {
        showSpinner();
        return queryClient
            .invalidateQueries({
                queryKey: trpc.user.fetchPortfolioBacktests.queryOptions({ portfolioId: portfolioId || 0 }).queryKey
            })
            .finally(() => {
                hideSpinner();
            });
    }, [portfolioId, queryClient]);

    // Show loading spinner when queries are loading
    useEffect(() => {
        const isLoading = backtestsQuery.isLoading || portfolioQuery.isLoading || requisitesLoading || allPortfoliosLoading;

        if (isLoading) {
            showSpinner();
        } else {
            hideSpinner();
        }

        return () => {
            hideSpinner();
        };
    }, [backtestsQuery.isLoading, portfolioQuery.isLoading, requisitesLoading, allPortfoliosLoading]);

    // Start backtest mutation
    const startBacktestMutation = useTrpcMutation(
        trpc.user.startPortfolioBacktest.mutationOptions({
            onSuccess: () => {
                // Invalidate backtests query to refresh data
                void refetchBacktests();
            }
        }),
        {
            successMessage: 'Backtest started successfully',
            fallbackErrorMessage: 'Failed to start backtest'
        }
    );

    // Handle new backtest submission
    const [futuresDrafts, setFuturesDrafts] = useState(() => [emptyFuturesConfiguration()]);
    const isFuturesSelection =
        modalPortfolio?.strategiesSubscribed?.some(
            (strategy) =>
                includedStrategyIds.has(strategy.id) &&
                (strategy.allowedUnderlyingEntityTypes.includes('FuturesProduct') ||
                    strategy.legs.some((leg) => leg.derivativeType === 'Future'))
        ) ?? false;
    const handleStartBacktest = async () => {
        if (isStartingBacktest.current) return;

        if (!portfolioId && !selectedPortfolioId) {
            showToast('Please select a portfolio', 'error');
            return;
        }

        if (!startDate || !endDate) {
            showToast('Please select both start and end dates', 'error');
            return;
        }

        if (startingBalance === null || startingBalance <= 0) {
            showToast('Starting capital must be greater than zero', 'error');
            return;
        }
        if (
            taxesEnabled &&
            (federalTaxRate === null ||
                stateTaxRate === null ||
                !Number.isFinite(federalTaxRate) ||
                !Number.isFinite(stateTaxRate) ||
                federalTaxRate < 0 ||
                stateTaxRate < 0 ||
                federalTaxRate + stateTaxRate > 100 ||
                isFuturesSelection)
        ) {
            showToast('Enter valid federal and state rates totaling at most 100%. Futures tax estimates are unsupported.', 'error');
            return;
        }

        if (needsBacktestReconstructionAcknowledgement) {
            showToast('Acknowledge the reconstruction lookahead before starting this research-only backtest', 'error');
            return;
        }

        showSpinner();
        isStartingBacktest.current = true;

        try {
            const includedStrategyIdList = Array.from(includedStrategyIds);

            if (includedStrategyIdList.length === 0) {
                showToast('Select at least one strategy', 'error');
                return;
            }

            const missingSettingsStrategyNames = includedStrategyIdList
                .filter((strategyId) => !strategySettings[strategyId]?.settingsId)
                .map(
                    (strategyId) => modalPortfolio?.strategiesSubscribed?.find((strategy) => strategy.id === strategyId)?.name ?? strategyId
                );

            if (missingSettingsStrategyNames.length > 0) {
                showToast(`Choose settings for: ${missingSettingsStrategyNames.join(', ')}`, 'error');
                return;
            }

            if (!exitCandidatePreflightQuery.data || exitCandidatePreflightQuery.isLoading) {
                showToast('Wait for strategy selection validation to finish', 'error');
                return;
            }

            const preflightErrors = [
                ...exitCandidatePreflightQuery.data.errorMessages,
                ...exitCandidatePreflightQuery.data.strategies.flatMap((strategy) => strategy.coverage.errorMessages)
            ];

            if (preflightErrors.length > 0 || hasBlockingExitCandidateCoverage) {
                showToast(preflightErrors[0] || 'Resolve the strategy backtest coverage errors before starting', 'error');
                return;
            }

            const result = await startBacktestMutation.mutateAsync({
                ...(isFuturesSelection ? { futuresConfigurations: futuresDrafts.map(parseFuturesConfiguration) } : {}),
                portfolioId: selectedPortfolioId || portfolioId || 0,
                startDateTime: startDate,
                endDateTime: endDate,
                trainingEndDateTime: trainingEndDate,
                name: backtestName,
                startingBalance,
                ...(taxesEnabled && federalTaxRate !== null && stateTaxRate !== null
                    ? { taxConfiguration: { federalRatePercent: federalTaxRate, stateRatePercent: stateTaxRate } }
                    : {}),
                includedStrategyIds: includedStrategyIdList,
                tradingProfilesForStrategies: portfolioBacktestStrategySelections,
                capitalControls: {
                    directionMaxRules: backtestDirectionMaxRules,
                    positionSizeRules: backtestPositionSizeRules
                },
                shouldLog,
                orchestratorPriority,
                bypassCache,
                backtestSelectionArtifactUniverseMode: usesCohortBestBacktestSelection
                    ? backtestSelectionArtifactUniverseMode
                    : 'strict_operational'
            });

            if (result.success && result.backtestId) {
                showToast(`Backtest ${result.backtestId} started`, 'success');
                setShowNewBacktestModal(false);
                resetBacktestForm();
            }
        } catch (err) {
            console.error('Error starting backtest:', err);
            // Error handling is done by useTrpcMutation
        } finally {
            isStartingBacktest.current = false;
            hideSpinner();
        }
    };

    // Apply a research-window preset: in-sample training (options start → development cutoff),
    // sealed walk-forward (cutoff → latest safe market date), or the full range.
    const applyResearchWindow = (mode: 'training' | 'walkforward' | 'full') => {
        const researchWindow = getResearchWindow(OPTIONS_DATA_START_DATETIME, new Date());
        setStartDate(mode === 'walkforward' ? researchWindow.walkForwardStart : researchWindow.trainingStart);
        setEndDate(mode === 'training' ? researchWindow.trainingEnd : researchWindow.walkForwardEnd);
        setTrainingEndDate(researchWindow.walkForwardStart);
    };

    // Reset backtest form
    const resetBacktestForm = useCallback(() => {
        initializedBacktestForm.current = null;
        setBacktestName('');
        setStartDate(OPTIONS_DATA_START_DATETIME);
        setEndDate(getLatestSafeMarketDateTime());
        setTrainingEndDate(getResearchWindow(OPTIONS_DATA_START_DATETIME, new Date()).walkForwardStart);
        setStartingBalance(DEFAULT_PORTFOLIO_BALANCE);
        setTaxesEnabled(false);
        setFederalTaxRate(0);
        setStateTaxRate(0);
        setStrategySettings({});
        setIncludedStrategyIds(new Set());
        setBacktestDirectionMaxRules([]);
        setBacktestPositionSizeRules([]);
        setBacktestSelectionArtifactUniverseMode('strict_operational');
        setHasAcknowledgedBacktestReconstruction(false);
        setSelectedPortfolioId(portfolioId);
        setDuplicateSourceBacktest(null);
    }, [portfolioId]);

    const openNewBacktestModal = useCallback(() => {
        resetBacktestForm();
        setShowNewBacktestModal(true);
    }, [resetBacktestForm]);

    const openDuplicateBacktestModal = useCallback(
        (backtest: PortfolioBacktestType) => {
            resetBacktestForm();
            setDuplicateSourceBacktest(backtest);
            setSelectedPortfolioId(backtest.portfolioId);
            setShowNewBacktestModal(true);
        },
        [resetBacktestForm]
    );

    // Wait for initial portfolio data, then initialize once for this open/portfolio/source.
    // Query refreshes can replace the portfolio object while the user is reviewing the form.
    useEffect(() => {
        if (!showNewBacktestModal) {
            initializedBacktestForm.current = null;
            return;
        }

        const requestedPortfolioId = selectedPortfolioId ?? portfolioId;
        if (initializedBacktestForm.current?.portfolioId !== requestedPortfolioId) initializedBacktestForm.current = null;
        if (!modalPortfolio?.strategiesSubscribed || modalPortfolio.id !== requestedPortfolioId) return;

        const duplicateBacktestId = duplicateSourceBacktest?.id ?? null;
        if (
            initializedBacktestForm.current?.portfolioId === modalPortfolio.id &&
            initializedBacktestForm.current.duplicateBacktestId === duplicateBacktestId
        ) {
            return;
        }

        initializedBacktestForm.current = { portfolioId: modalPortfolio.id, duplicateBacktestId };
        setTaxesEnabled(Boolean(duplicateSourceBacktest?.taxConfiguration));
        setFederalTaxRate(duplicateSourceBacktest?.taxConfiguration?.federalRatePercent ?? 0);
        setStateTaxRate(duplicateSourceBacktest?.taxConfiguration?.stateRatePercent ?? 0);
        if (duplicateFormState) {
            setStrategySettings(duplicateFormState.strategySettings);
            setIncludedStrategyIds(new Set(duplicateFormState.includedStrategyIds));
        } else {
            const initialSettings: Record<number, PortfolioBacktestFormStrategySetting> = {};
            const initialIncludedStrategyIds = new Set<number>();

            modalPortfolio.strategiesSubscribed.forEach((strategy) => {
                initialIncludedStrategyIds.add(strategy.id);

                if (strategy.tradingProfileId) {
                    initialSettings[strategy.id] = {
                        settingsId: strategy.tradingProfileId,
                        optionBuyingStrategyIds: [...(strategy.optionBuyingStrategyIds ?? [])],
                        exitCandidateIds: [...(strategy.exitCandidateIds ?? [])]
                    };
                }
            });

            setStrategySettings(initialSettings);
            setIncludedStrategyIds(initialIncludedStrategyIds);
        }

        setBacktestDirectionMaxRules(structuredClone(modalPortfolio.settings?.directionMaxRules ?? []));
        setBacktestPositionSizeRules(
            duplicateFormState ? duplicateFormState.positionSizeRules : structuredClone(modalPortfolio.settings?.positionSizeRules ?? [])
        );
        // Reset advanced options to defaults
        setShouldLog(false);
        setOrchestratorPriority(8);
        setBypassCache(false);
        setBacktestSelectionArtifactUniverseMode('strict_operational');
        setHasAcknowledgedBacktestReconstruction(false);
    }, [duplicateFormState, duplicateSourceBacktest?.id, showNewBacktestModal, modalPortfolio, selectedPortfolioId, portfolioId]);

    // Delete backtests mutation
    const deleteBacktestsMutation = useTrpcMutation(
        trpc.backtest.deleteItems.mutationOptions({
            onSuccess: () => {
                // Invalidate backtests query to refresh data
                void refetchBacktests();
            }
        }),
        {
            successMessage: 'Backtests deleted successfully',
            fallbackErrorMessage: 'Failed to delete backtests'
        }
    );

    const clearSelection = useCallback(() => {
        setSelectedRows({});
    }, []);

    const requestDeleteBacktests = useCallback((ids: number[], label: string) => {
        if (ids.length === 0) {
            showToast('No backtests selected', 'info');
            return;
        }

        setPendingDeleteIds(ids);
        setPendingDeleteLabel(label);
    }, []);

    const handleDeleteSelected = useCallback(() => {
        const selectedIds = Object.entries(selectedRows)
            .filter(([_, isSelected]) => isSelected)
            .map(([id, _]) => parseInt(id, 10));

        requestDeleteBacktests(selectedIds, `${selectedIds.length} selected backtest${selectedIds.length === 1 ? '' : 's'}`);
    }, [requestDeleteBacktests, selectedRows]);

    const confirmDeleteBacktests = useCallback(async () => {
        if (pendingDeleteIds.length === 0) return;

        try {
            await deleteBacktestsMutation.mutateAsync({
                ids: pendingDeleteIds,
                type: 'portfolioBacktest'
            });

            setSelectedRows((prev) => {
                const nextRows = { ...prev };

                pendingDeleteIds.forEach((backtestId) => {
                    delete nextRows[backtestId];
                });

                return nextRows;
            });

            setPendingDeleteIds([]);
            setPendingDeleteLabel('');
        } catch (err) {
            console.error('Error deleting backtests:', err);
            // Error handling is done by useTrpcMutation
        }
    }, [deleteBacktestsMutation, pendingDeleteIds]);

    const cancelDeleteBacktests = useCallback(() => {
        if (deleteBacktestsMutation.isPending) return;

        setPendingDeleteIds([]);
        setPendingDeleteLabel('');
    }, [deleteBacktestsMutation.isPending]);

    const selectFilteredBacktests = useCallback((backtestsToSelect: PortfolioBacktestType[]) => {
        if (backtestsToSelect.length === 0) {
            showToast('No backtests match the current filters', 'info');
            return;
        }

        setSelectedRows((prev) => {
            const nextRows = { ...prev };

            backtestsToSelect.forEach((backtest) => {
                nextRows[backtest.id] = true;
            });

            return nextRows;
        });
    }, []);

    // Show settings for a strategy
    const showStrategySettings = useCallback((settings: TradingProfile) => {
        setCurrentSettings(settings);
        setShowSettingsModal(true);
    }, []);

    const renderFrozenRunConfiguration = useCallback(
        ({ row }: { row: Row<PortfolioBacktestType> }) => (
            <FrozenRunConfiguration
                backtest={row.original}
                drift={getFrozenDrift(row.original.id)}
                exitCandidateLabelMap={exitCandidateLabelMap}
                optionBuyingStrategyNameMap={optionBuyingStrategyNameMap}
                strategyNameMap={strategyNameMap}
                onViewTradingProfile={showStrategySettings}
            />
        ),
        [exitCandidateLabelMap, getFrozenDrift, optionBuyingStrategyNameMap, showStrategySettings, strategyNameMap]
    );

    // Handle strategy setting change
    const handleStrategySettingChange = (strategyId: number, settingsId: number | null) => {
        if (settingsId === null || Number.isNaN(settingsId)) {
            setStrategySettings((prev) => {
                const nextSettings = { ...prev };

                delete nextSettings[strategyId];
                return nextSettings;
            });

            return;
        }

        setStrategySettings((prev) => ({
            ...prev,
            [strategyId]: {
                ...prev[strategyId],
                settingsId,
                optionBuyingStrategyIds: prev[strategyId]?.optionBuyingStrategyIds ?? [
                    ...(modalPortfolio?.strategiesSubscribed?.find((strategy) => strategy.id === strategyId)?.optionBuyingStrategyIds ?? [])
                ],
                exitCandidateIds: prev[strategyId]?.exitCandidateIds ?? [
                    ...(modalPortfolio?.strategiesSubscribed?.find((strategy) => strategy.id === strategyId)?.exitCandidateIds ?? [])
                ]
            }
        }));
    };

    const handleStrategyIncludedChange = (strategyId: number, included: boolean) => {
        setIncludedStrategyIds((prev) => {
            const next = new Set(prev);

            if (included) {
                next.add(strategyId);
            } else {
                next.delete(strategyId);
            }

            return next;
        });
    };

    const includeAllSubscribedStrategies = () => {
        setIncludedStrategyIds(new Set(modalPortfolio?.strategiesSubscribed?.map((strategy) => strategy.id) ?? []));
    };

    const includeActiveSubscribedStrategies = () => {
        setIncludedStrategyIds(
            new Set(modalPortfolio?.strategiesSubscribed?.filter((strategy) => strategy.isActive).map((strategy) => strategy.id) ?? [])
        );
    };

    const availableStrategyIds = useMemo(() => {
        const ids = new Set<number>();

        backtests.forEach((backtest) => {
            backtest.strategiesSubscribed.forEach((strategy) => {
                if (strategy.id) ids.add(strategy.id);
            });
        });

        return ids;
    }, [backtests]);

    const isStrategyInBacktests = useCallback((strategy: TradingStrategy) => availableStrategyIds.has(strategy.id), [availableStrategyIds]);

    const filteredBacktests = useMemo(() => {
        const searchTerms = deferredSearchQuery.trim().toLowerCase().split(/\s+/).filter(Boolean);

        return backtests.filter((backtest) => {
            if (!matchesPortfolioRefreshFilter(resolvePortfolioRefreshStatus(backtest, refreshStatusByBaseline), refreshFilter))
                return false;
            if (!portfolioId && selectedPortfolioFilter !== null && backtest.portfolioId !== selectedPortfolioFilter) {
                return false;
            }

            if (selectedStatusFilter !== 'all') {
                if (selectedStatusFilter === 'capitalStarved') {
                    if (!backtest.diagnostics?.capitalStarvation.isDetected) return false;
                } else if (backtest.status !== selectedStatusFilter) {
                    return false;
                }
            }

            if (
                selectedStrategyFilter !== 'all' &&
                !backtest.strategiesSubscribed.some((strategy) => strategy.id === parseInt(selectedStrategyFilter, 10))
            ) {
                return false;
            }

            if (searchTerms.length > 0) {
                const searchableText = [
                    backtest.id.toString(),
                    backtest.name,
                    backtest.status,
                    portfolioMap.get(backtest.portfolioId)?.name || '',
                    ...backtest.strategiesSubscribed.map((strategy) => strategyNameMap.get(strategy.id) || ''),
                    ...backtest.strategiesSubscribed.map((strategy) => strategy.tradingProfile?.name || ''),
                    // Frozen OBS and exit ids are only listed in the expanded row, so keep them searchable here.
                    ...buildFrozenSnapshotSearchTokens(backtest)
                ]
                    .join(' ')
                    .toLowerCase();

                return searchTerms.every((term) => searchableText.includes(term));
            }

            return true;
        });
    }, [
        backtests,
        deferredSearchQuery,
        portfolioId,
        portfolioMap,
        selectedPortfolioFilter,
        refreshFilter,
        refreshStatusByBaseline,
        selectedStatusFilter,
        selectedStrategyFilter,
        strategyNameMap
    ]);

    const totalSelectedCount = useMemo(() => Object.values(selectedRows).filter(Boolean).length, [selectedRows]);

    const filteredSelectedCount = useMemo(
        () => filteredBacktests.filter((backtest) => selectedRows[backtest.id]).length,
        [filteredBacktests, selectedRows]
    );

    const allFilteredSelected = filteredBacktests.length > 0 && filteredSelectedCount === filteredBacktests.length;

    const hasActiveFilters =
        searchQuery.trim().length > 0 ||
        selectedStatusFilter !== 'all' ||
        refreshFilter !== 'all' ||
        selectedStrategyFilter !== 'all' ||
        selectedPortfolioFilter !== null;

    const backtestSummary = useMemo(() => {
        return backtests.reduce(
            (summary, backtest) => {
                summary.total += 1;

                if (backtest.status === 'running' || backtest.status === 'queued') {
                    summary.active += 1;
                }

                if (backtest.status === 'failed') {
                    summary.failed += 1;
                }

                if (backtest.diagnostics?.capitalStarvation.isDetected) {
                    summary.capitalStarved += 1;
                }

                return summary;
            },
            { total: 0, active: 0, failed: 0, capitalStarved: 0 }
        );
    }, [backtests]);

    // Drop selections for backtests that no longer exist after a refetch (e.g. deleted). This is an
    // effect because it must react to the async backtests list changing.
    useEffect(() => {
        const liveBacktestIds = new Set(backtests.map((backtest) => backtest.id.toString()));

        setSelectedRows((prev) => {
            const nextRows = Object.entries(prev).reduce<Record<string, boolean>>((accumulator, [backtestId, isSelected]) => {
                if (isSelected && liveBacktestIds.has(backtestId)) {
                    accumulator[backtestId] = true;
                }

                return accumulator;
            }, {});

            const prevKeys = Object.keys(prev);
            const nextKeys = Object.keys(nextRows);

            if (prevKeys.length === nextKeys.length && prevKeys.every((key) => nextRows[key] === prev[key])) {
                return prev;
            }

            return nextRows;
        });
    }, [backtests]);

    const clearFilters = useCallback(() => {
        setSearchQuery('');
        setSelectedStatusFilter('all');
        setRefreshFilter('all');
        setSelectedStrategyFilter('all');
        setSelectedPortfolioFilter(null);
    }, []);

    // Icon-only so the pinned actions column stays narrow; each button names its action in its label and tooltip.
    const rowActionButtonClass = 'h-6 w-6 !p-0';

    const applyColumnPreset = useCallback((preset: keyof typeof portfolioBacktestColumnPresets) => {
        setVisibleColumns(new Set(portfolioBacktestColumnPresets[preset]));
    }, []);

    const resetVisibleColumns = useCallback(() => {
        setVisibleColumns(getDefaultPortfolioBacktestColumns());
    }, []);

    /** Columns that hold one value across every filtered row, so they cost width without separating anything. */
    const uniformColumnIds = useMemo(() => findUniformColumnIds(filteredBacktests), [filteredBacktests]);

    const hideableUniformColumnIds = useMemo(
        () => new Set([...visibleColumns].filter((columnId) => uniformColumnIds.has(columnId))),
        [uniformColumnIds, visibleColumns]
    );

    const hideUniformColumns = useCallback(() => {
        const remainingColumns = [...visibleColumns].filter((columnId) => !uniformColumnIds.has(columnId));

        // The table must keep a data column; hiding every uniform column would otherwise empty it.
        if (remainingColumns.length === 0) {
            return;
        }

        setVisibleColumns(new Set(remainingColumns));
    }, [uniformColumnIds, visibleColumns]);

    const enabledColumnIds = useMemo(() => {
        return new Set<string>([...visibleColumns, 'backtest', 'refresh', 'actions', 'select']);
    }, [visibleColumns]);

    // Every row shares one portfolio on a portfolio's own page or under the portfolio filter, so the line would only repeat.
    const showsPortfolioInIdentity = !portfolioId && selectedPortfolioFilter === null;

    // Table columns for backtests
    const columns = useMemo<ColumnDef<PortfolioBacktestType>[]>(() => {
        const allColumns: ColumnDef<PortfolioBacktestType>[] = [
            {
                header: 'ID',
                accessorKey: 'id',
                cell: (info) => info.getValue(),
                minSize: 56,
                size: 64,
                maxSize: 80,
                meta: { sticky: 'left' }
            },
            {
                header: 'Backtest',
                id: 'backtest',
                accessorFn: (row) => row.name,
                sortFn: compareBacktestNames,
                cell: ({ row }) => {
                    const backtest = row.original;

                    return (
                        <div>
                            <PortfolioBacktestIdentityCell
                                backtest={backtest}
                                drift={getFrozenDrift(backtest.id)}
                                exitCandidateLabelMap={exitCandidateLabelMap}
                                optionBuyingStrategyNameMap={optionBuyingStrategyNameMap}
                                portfolio={
                                    showsPortfolioInIdentity
                                        ? { id: backtest.portfolioId, name: portfolioMap.get(backtest.portfolioId)?.name }
                                        : null
                                }
                                strategyNameMap={strategyNameMap}
                            />
                        </div>
                    );
                },
                /*
                 * Pinned so the run stays identifiable while the stat columns scroll beneath it. minSize,
                 * not size: under auto table layout a wrappable text column is the first thing the browser
                 * compresses once the table overflows, so `size` alone gets ignored. 320px holds the longest
                 * run name in use (72 characters) within the two-line clamp.
                 */
                minSize: 320,
                size: 340,
                maxSize: 380,
                meta: { sticky: 'left' }
            },
            {
                id: 'refresh',
                header: 'Auto refresh',
                accessorFn: (backtest) => (resolvePortfolioRefreshStatus(backtest, refreshStatusByBaseline)?.isEnabled ? 'On' : 'Off'),
                cell: ({ row }) =>
                    refreshSchedulesQuery.data ? (
                        <PortfolioBacktestRefreshSummary
                            backtestId={row.original.id}
                            status={resolvePortfolioRefreshStatus(row.original, refreshStatusByBaseline)}
                        />
                    ) : (
                        <span className="text-xs text-zinc-400">{refreshSchedulesQuery.isLoading ? 'Loading…' : 'Unavailable'}</span>
                    ),
                minSize: 185,
                size: 210
            },
            {
                header: 'Capital',
                id: 'capital',
                minSize: 160,
                size: 200,
                maxSize: 280,
                accessorFn: (row) => summarizeFrozenCapital(row),
                cell: (info) => <span className="text-xs text-zinc-300">{info.getValue<string>()}</span>
            },
            {
                header: 'Status',
                minSize: 72,
                size: 88,
                maxSize: 120,
                accessorKey: 'status',
                cell: ({ row }) => {
                    const backtest = row.original;
                    const isCapitalStarved = backtest.diagnostics?.capitalStarvation.isDetected ?? false;

                    return (
                        <div className="flex flex-col">
                            <span>{backtest.status}</span>
                            {isCapitalStarved && <span className="text-xs text-amber-400">capital starved</span>}
                        </div>
                    );
                }
            },
            {
                header: 'Trades',
                accessorKey: 'numTrades',
                minSize: 64,
                size: 76,
                maxSize: 96,
                cell: ({ row }) => {
                    const { id: backtestId, numTrades } = row.original;

                    return (
                        <Link
                            className="text-blue-300 hover:text-blue-200"
                            title={`View ${numTrades.toLocaleString()} trades`}
                            to={`/user/portfolioBacktestTrades?id=${backtestId}`}
                        >
                            {numTrades.toLocaleString()}
                        </Link>
                    );
                }
            },
            {
                header: 'Start Bal',
                accessorKey: 'startingBalance',
                minSize: 92,
                size: 112,
                maxSize: 128,
                cell: (info) => `$${(info.getValue() as number).toLocaleString()}`
            },
            {
                header: 'End Bal',
                id: 'adjustedEndingBalance',
                minSize: 92,
                size: 116,
                maxSize: 136,
                cell: ({ row }) => {
                    const backtest = row.original;
                    return `$${(backtest.endingBalance - backtest.replenishmentAmount).toLocaleString()}`;
                }
            },
            {
                header: 'Repl. Cnt',
                accessorKey: 'replenishmentCount',
                minSize: 60,
                size: 72,
                maxSize: 88,
                cell: (info) => (info.getValue() as number).toLocaleString()
            },
            {
                header: 'Repl. Amt',
                accessorKey: 'replenishmentAmount',
                minSize: 84,
                size: 96,
                maxSize: 112,
                cell: (info) => `$${(info.getValue() as number).toLocaleString()}`
            },
            {
                header: '% Change',
                minSize: 72,
                size: 84,
                maxSize: 96,
                id: 'percentageChange',
                cell: ({ row }) => {
                    const backtest = row.original;

                    const percentageChange = calculatePercentageChangeFast(
                        backtest.startingBalance,
                        backtest.endingBalance - backtest.replenishmentAmount
                    );

                    const changeColorClass =
                        percentageChange > 0
                            ? 'text-profit-green !text-[#29a740]'
                            : percentageChange < 0
                              ? 'text-red-500'
                              : 'text-zinc-400';

                    return <span className={changeColorClass}>{formatBacktestPercent(percentageChange)}</span>;
                }
            },
            {
                header: 'CAGR',
                id: 'cagr',
                minSize: 72,
                size: 84,
                maxSize: 100,
                accessorFn: (row) => calculatePortfolioCagr(row) ?? undefined,
                sortFn: 'basic',
                sortUndefined: 'last',
                cell: ({ row }) => {
                    const value = calculatePortfolioCagr(row.original);
                    return (
                        <span title="Annualized portfolio growth over the saved valuation period, excluding external replenishments. Unavailable without saved market-valued returns.">
                            {value === null ? '—' : `${value.toFixed(2)}%`}
                        </span>
                    );
                }
            },
            {
                header: 'ARORC',
                accessorKey: 'returnOnRiskCapital',
                minSize: 72,
                size: 84,
                maxSize: 100,
                cell: (info) => {
                    const value = info.getValue() as number;

                    const changeColorClass = value > 0 ? 'text-profit-green !text-[#29a740]' : value < 0 ? 'text-red-500' : 'text-zinc-400';

                    return <span className={changeColorClass}>{formatBacktestPercent(value)}</span>;
                }
            },
            {
                // Sharpe ratio will be returned by backend soon; make this column tolerant to absence
                header: 'Sharpe',
                id: 'sharpeRatio',
                minSize: 56,
                size: 68,
                maxSize: 80,
                accessorFn: (row) => row.sharpeRatio ?? null,
                cell: ({ row }) => {
                    const v = row.original.sharpeRatio;
                    return v ? v.toFixed(2) : '-';
                }
            },
            {
                // Largest peak-to-trough decline of the equity curve, stored as a negative percent
                header: 'Max DD',
                id: 'maxDrawdown',
                minSize: 64,
                size: 76,
                maxSize: 92,
                accessorFn: (row) => row.maxDrawdown ?? null,
                cell: ({ row }) => {
                    const value = row.original.maxDrawdown;

                    if (value == null) return '-';

                    return <span className={value < 0 ? 'text-red-500' : 'text-zinc-400'}>{formatBacktestPercent(value)}</span>;
                }
            },
            {
                // Context benchmark (ticker quality / market), not a random-entry control
                header: 'SPY %',
                id: 'spyReturnPercent',
                minSize: 72,
                size: 84,
                maxSize: 96,
                accessorFn: (row) => row.spyReturnPercent ?? null,
                cell: ({ row }) => {
                    const value = row.original.spyReturnPercent;

                    if (value == null) return '-';

                    const changeColorClass = value > 0 ? 'text-profit-green !text-[#29a740]' : value < 0 ? 'text-red-500' : 'text-zinc-400';

                    return <span className={changeColorClass}>{formatBacktestPercent(value)}</span>;
                }
            },
            {
                // Context benchmark (ticker quality / market), not a random-entry control
                header: 'Universe B&H %',
                id: 'universeBuyHoldReturnPercent',
                minSize: 84,
                size: 100,
                maxSize: 120,
                accessorFn: (row) => row.universeBuyHoldReturnPercent ?? null,
                cell: ({ row }) => {
                    const value = row.original.universeBuyHoldReturnPercent;

                    if (value == null) return '-';

                    const changeColorClass = value > 0 ? 'text-profit-green !text-[#29a740]' : value < 0 ? 'text-red-500' : 'text-zinc-400';

                    return <span className={changeColorClass}>{formatBacktestPercent(value)}</span>;
                }
            },
            {
                // Random-entry CONTROL: exposure-matched mean per-trade edge vs random same-name/month/horizon entries.
                // Supported-structure + gross-vs-net caveats live in the shared disclaimer tooltip (design §6).
                header: () => <span title={TIMING_EDGE_DISCLAIMER}>Timing Edge %</span>,
                id: 'timingEdgePercent',
                minSize: 84,
                size: 100,
                maxSize: 120,
                accessorFn: (row) => row.timingEdgePercent ?? null,
                cell: ({ row }) => {
                    const value = row.original.timingEdgePercent;
                    const tooltip = getTimingEdgeTooltip(row.original);

                    if (value == null)
                        return (
                            <span aria-label={tooltip} title={tooltip}>
                                -
                            </span>
                        );

                    const changeColorClass = value > 0 ? 'text-profit-green !text-[#29a740]' : value < 0 ? 'text-red-500' : 'text-zinc-400';

                    return (
                        <span aria-label={tooltip} className={changeColorClass} title={tooltip}>
                            {formatBacktestPercent(value)}
                        </span>
                    );
                }
            },
            {
                // Walk-forward holdout verdict + the portfolio's own return in the sealed OOS window.
                // Only runs completed after forward evaluation shipped carry one; older rows read '-'.
                header: () => <span title={WALK_FORWARD_DISCLAIMER}>Walk-Forward</span>,
                id: 'walkForward',
                minSize: 108,
                size: 124,
                maxSize: 148,
                accessorFn: (row) => row.diagnostics?.forwardEvaluation?.portfolio.walkForward?.avgProfitPercent ?? null,
                cell: ({ row }) => <PortfolioBacktestWalkForwardCell forwardEvaluation={row.original.diagnostics?.forwardEvaluation} />
            },
            {
                header: 'Start',
                accessorKey: 'startDateTime',
                minSize: 96,
                size: 112,
                maxSize: 124,
                cell: (info) => getDate({ date: info.getValue() as string, format: 'readable2' })
            },
            {
                header: 'End',
                accessorKey: 'endDateTime',
                minSize: 96,
                size: 112,
                maxSize: 124,
                cell: (info) => getDate({ date: info.getValue() as string, format: 'readable2' })
            },
            {
                header: 'Actions',
                id: 'actions',
                minSize: 116,
                size: 132,
                maxSize: 148,
                meta: {
                    className: 'align-middle',
                    headerClassName: 'whitespace-nowrap',
                    sticky: 'right'
                },
                /*
                 * Kept on one line: stacking the row actions sets the row-height floor for every row,
                 * including the majority that are never acted on.
                 */
                cell: ({ row }) => {
                    const backtest = row.original;

                    return (
                        <div className="flex items-center gap-1 whitespace-nowrap">
                            {refreshSchedulesQuery.data && (
                                <PortfolioBacktestRefreshControl
                                    backtest={backtest}
                                    status={resolvePortfolioRefreshStatus(backtest, refreshStatusByBaseline)}
                                    onChanged={refreshSchedulesChanged}
                                    className={rowActionButtonClass}
                                />
                            )}
                            <Button
                                className={rowActionButtonClass}
                                linkProps={{ 'aria-label': `View stats for backtest ${backtest.id}`, title: 'View stats' }}
                                size="sm"
                                to={`/user/portfolioBacktestStats?id=${backtest.id}`}
                                variant="dark"
                            >
                                <FaChartBar aria-hidden="true" className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                                aria-label={`Duplicate configuration from backtest ${backtest.id}`}
                                className={rowActionButtonClass}
                                size="sm"
                                title="Duplicate OBS, exits, and position sizing into a new backtest"
                                variant="dark"
                                onClick={() => openDuplicateBacktestModal(backtest)}
                            >
                                <FaClone aria-hidden="true" className="h-3.5 w-3.5" />
                            </Button>
                            <DeleteButton
                                aria-label={`Delete backtest ${backtest.id}`}
                                className={rowActionButtonClass}
                                size="sm"
                                title="Delete backtest"
                                onClick={() =>
                                    requestDeleteBacktests(
                                        [backtest.id],
                                        backtest.name ? `"${backtest.name}"` : `backtest #${backtest.id.toString()}`
                                    )
                                }
                            />
                        </div>
                    );
                }
            },
            {
                header: 'Select',
                id: 'select',
                minSize: 52,
                size: 60,
                maxSize: 60,
                meta: {
                    className: 'text-center',
                    headerClassName: 'text-center',
                    sticky: 'right'
                },
                cell: ({ row }) => (
                    <FormCheckbox
                        checked={selectedRows[row.original.id] || false}
                        onChange={(e) => {
                            setSelectedRows((prev) => ({
                                ...prev,
                                [row.original.id]: e.target.checked
                            }));
                        }}
                    />
                )
            }
        ];

        return allColumns.filter((column) => {
            const columnId = getTableColumnId(column);

            return columnId ? enabledColumnIds.has(columnId) : true;
        });
    }, [
        enabledColumnIds,
        exitCandidateLabelMap,
        getFrozenDrift,
        optionBuyingStrategyNameMap,
        openDuplicateBacktestModal,
        portfolioMap,
        requestDeleteBacktests,
        refreshStatusByBaseline,
        refreshSchedulesQuery.data,
        refreshSchedulesQuery.isLoading,
        refreshSchedulesChanged,
        rowActionButtonClass,
        selectedRows,
        showStrategySettings,
        showsPortfolioInIdentity,
        strategyNameMap
    ]);

    // Determine if data is loading
    const isLoading = backtestsQuery.isLoading || portfolioQuery.isLoading || requisitesLoading || allPortfoliosLoading;

    // Render loading state
    if (isLoading) {
        return (
            <UserLayout
                title={
                    portfolio?.name ? `${portfolio.name} - Backtest - Loading - Project 314` : 'Portfolio Backtest - Loading - Project 314'
                }
            >
                <div className="flex justify-center items-center min-h-[40vh]">
                    <DataLoadingSpinner text="Loading backtests..." />
                </div>
            </UserLayout>
        );
    }

    // Render error state
    if (error) {
        return (
            <UserLayout
                title={portfolio?.name ? `${portfolio.name} - Backtest - Error - Project 314` : 'Portfolio Backtest - Error - Project 314'}
            >
                <div className="bg-red-100 text-red-800 border border-red-200 p-4 mb-4 rounded-md">{error}</div>
            </UserLayout>
        );
    }

    return (
        <UserLayout
            containerSize="2000px"
            title={portfolio?.name ? `${portfolio.name} - Backtest - Project 314` : 'Portfolio Backtest - Project 314'}
        >
            <div className="portfolio-backtest-page">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="mb-0 text-white text-xl font-semibold">
                        {portfolio ? (
                            <>
                                <Link className="text-primary hover:text-blue-400" to={`/user/portfolio/${portfolio.id}`}>
                                    {portfolio.name} ({capitalizeFirstLetter(portfolio.type || '')})
                                </Link>
                                {' - Backtests'}
                            </>
                        ) : (
                            'All Portfolio Backtests'
                        )}
                    </h3>
                    <Button icon={<FaPlus aria-hidden="true" />} variant="primary" onClick={openNewBacktestModal}>
                        New Backtest
                    </Button>
                </div>

                <Card
                    responsivePadding
                    bodyClassName="space-y-4"
                    className="mb-4"
                    headerActions={
                        <div className="text-sm text-zinc-400">
                            Showing {filteredBacktests.length.toLocaleString()} of {backtests.length.toLocaleString()} backtests
                        </div>
                    }
                    subtitle="Filters and pagination are client-side for now. True load reduction still requires server-side pagination."
                    title="Manage Backtests"
                >
                    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                        <div className="rounded-lg border border-zinc-800 bg-[#151515] p-3">
                            <div className="text-xs uppercase tracking-wide text-zinc-500">Total</div>
                            <div className="mt-1 text-2xl font-semibold text-white">{backtestSummary.total.toLocaleString()}</div>
                        </div>
                        <div className="rounded-lg border border-zinc-800 bg-[#151515] p-3">
                            <div className="text-xs uppercase tracking-wide text-zinc-500">Queued / Running</div>
                            <div className="mt-1 text-2xl font-semibold text-amber-300">{backtestSummary.active.toLocaleString()}</div>
                        </div>
                        <div className="rounded-lg border border-zinc-800 bg-[#151515] p-3">
                            <div className="text-xs uppercase tracking-wide text-zinc-500">Failed</div>
                            <div className="mt-1 text-2xl font-semibold text-red-400">{backtestSummary.failed.toLocaleString()}</div>
                        </div>
                        <div className="rounded-lg border border-zinc-800 bg-[#151515] p-3">
                            <div className="text-xs uppercase tracking-wide text-zinc-500">Capital Starved</div>
                            <div className="mt-1 text-2xl font-semibold text-orange-300">
                                {backtestSummary.capitalStarved.toLocaleString()}
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-end gap-4">
                        <div className="min-w-[180px] flex-1">
                            <FormInput
                                noMargin
                                label="Search"
                                placeholder="Name, ID, strategy, status..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>

                        {!portfolioId && (
                            <div className="min-w-[180px] flex-1">
                                <PortfolioSelect
                                    noMargin
                                    label="Portfolio"
                                    placeholder="All portfolios"
                                    value={selectedPortfolioFilter}
                                    onChange={setSelectedPortfolioFilter}
                                />
                            </div>
                        )}

                        <div className="min-w-[180px] flex-1">
                            <FormSelect
                                noMargin
                                label="Status"
                                options={[
                                    { value: 'all', label: 'All statuses' },
                                    { value: 'queued', label: 'Queued' },
                                    { value: 'running', label: 'Running' },
                                    { value: 'complete', label: 'Complete' },
                                    { value: 'failed', label: 'Failed' },
                                    { value: 'capitalStarved', label: 'Capital starved' }
                                ]}
                                value={selectedStatusFilter}
                                onChange={(e) => setSelectedStatusFilter(e.target.value as BacktestStatusFilter)}
                            />
                        </div>

                        <div className="min-w-[180px] flex-1">
                            <TradingStrategySelect
                                noMargin
                                filter={isStrategyInBacktests}
                                label="Strategy"
                                placeholder="All strategies"
                                value={selectedStrategyFilter === 'all' ? null : Number(selectedStrategyFilter)}
                                onChange={(strategyId) => setSelectedStrategyFilter(strategyId ? strategyId.toString() : 'all')}
                            />
                        </div>

                        <div className="flex items-end gap-2">
                            <PortfolioBacktestColumnVisibilityControl
                                availableColumns={portfolioBacktestColumnRegistry}
                                uniformColumnIds={hideableUniformColumnIds}
                                visibleColumns={visibleColumns}
                                onHideUniformColumns={hideUniformColumns}
                                onPresetSelect={applyColumnPreset}
                                onReset={resetVisibleColumns}
                                onVisibilityChange={setVisibleColumns}
                            />
                            <label className="text-sm text-zinc-300">
                                Auto refresh
                                <select
                                    className="ml-2 rounded bg-zinc-800 p-2"
                                    value={refreshFilter}
                                    disabled={!refreshSchedulesQuery.data}
                                    onChange={(event) => setRefreshFilter(event.target.value as PortfolioRefreshFilter)}
                                >
                                    <option value="all">All</option>
                                    <option value="enabled">On</option>
                                    <option value="paused">Paused</option>
                                    <option value="off">Off / never enrolled</option>
                                </select>
                            </label>
                            <Button disabled={!hasActiveFilters} variant="secondary" onClick={clearFilters}>
                                Clear Filters
                            </Button>
                            <Button
                                disabled={backtestsQuery.isFetching}
                                isLoading={backtestsQuery.isFetching}
                                variant="outline-dark"
                                onClick={refetchBacktests}
                            >
                                Refresh
                            </Button>
                        </div>

                        <div className="flex items-end">
                            <div className="text-sm text-zinc-400">
                                {totalSelectedCount > 0 ? `${totalSelectedCount.toLocaleString()} selected` : 'No backtests selected'}
                            </div>
                        </div>
                    </div>
                </Card>

                {filteredBacktests.length > 0 ? (
                    <DataTable
                        striped
                        columns={columns}
                        data={filteredBacktests}
                        getRowId={(backtest) => backtest.id.toString()}
                        pagination={{ enabled: filteredBacktests.length > 25, pageSize: 25 }}
                        // Scrolls inside the viewport so the header row and the horizontal scrollbar stay on screen.
                        maxHeight="calc(100dvh - 2rem)"
                        renderSubComponent={renderFrozenRunConfiguration}
                        search={false}
                    />
                ) : (
                    <Card responsivePadding className="mb-4">
                        <div className="text-zinc-300">
                            {backtests.length === 0
                                ? 'No backtests exist for this portfolio yet.'
                                : 'No backtests match the current filters.'}
                        </div>
                    </Card>
                )}

                <div className="flex justify-end mt-4 mb-4 space-x-2">
                    <Button
                        disabled={filteredBacktests.length === 0 || allFilteredSelected}
                        icon={<FaCheckSquare aria-hidden="true" />}
                        variant="secondary"
                        onClick={() => selectFilteredBacktests(filteredBacktests)}
                    >
                        {allFilteredSelected
                            ? `Filtered Selected (${filteredSelectedCount.toLocaleString()})`
                            : `Select Filtered (${filteredBacktests.length.toLocaleString()})`}
                    </Button>
                    <Button disabled={totalSelectedCount === 0} variant="outline-dark" onClick={clearSelection}>
                        Clear Selection
                    </Button>
                    <DeleteButton
                        showText
                        disabled={totalSelectedCount === 0}
                        text={`Delete Selected${totalSelectedCount > 0 ? ` (${totalSelectedCount.toLocaleString()})` : ''}`}
                        onClick={handleDeleteSelected}
                    />
                </div>

                {/* New Backtest Modal */}
                <Modal
                    footer={
                        <Button
                            isLoading={startBacktestMutation.isPending}
                            disabled={
                                includedStrategyIds.size === 0 ||
                                startingBalance === null ||
                                startingBalance <= 0 ||
                                portfolioBacktestStrategySelections.length !== includedStrategyIds.size ||
                                hasPortfolioBacktestPreflightError ||
                                needsBacktestReconstructionAcknowledgement
                            }
                            icon={<FaPlay aria-hidden="true" />}
                            variant="primary"
                            onClick={handleStartBacktest}
                        >
                            Start
                        </Button>
                    }
                    isOpen={showNewBacktestModal}
                    title={duplicateSourceBacktest ? 'Duplicate Backtest Configuration' : 'Launch New Backtest'}
                    onClose={() => setShowNewBacktestModal(false)}
                >
                    <div className="space-y-4">
                        {duplicateSourceBacktest && (
                            <Alert variant="info">
                                Using the portfolio&apos;s current linked trading profiles where available. OBS, exits, and position sizing
                                were copied from backtest #{duplicateSourceBacktest.id.toString()}. Review the selections before starting a
                                new run.
                            </Alert>
                        )}
                        {duplicateFormState && duplicateFormState.missingStrategyIds.length > 0 && (
                            <Alert variant="warning">
                                The following source strategies are no longer subscribed to this portfolio and were skipped:{' '}
                                {duplicateFormState.missingStrategyIds.join(', ')}.
                            </Alert>
                        )}
                        {duplicateStrategyIdsMissingSettings.length > 0 && (
                            <Alert variant="warning">
                                Choose settings for these subscribed source strategies before starting:{' '}
                                {duplicateStrategyIdsMissingSettings.join(', ')}.
                            </Alert>
                        )}
                        {duplicateSourceBacktest && duplicateProfileChangeMessages.length > 0 && (
                            <Alert variant="warning">
                                Trading profiles have changed since backtest #{duplicateSourceBacktest.id.toString()}. Copied OBS, exits,
                                and position sizing will be applied to the current profiles:
                                <ul className="mt-2 list-disc space-y-1 pl-5">
                                    {duplicateProfileChangeMessages.map((profileChange) => (
                                        <li key={profileChange.strategyId}>{profileChange.message}</li>
                                    ))}
                                </ul>
                            </Alert>
                        )}
                        <div className="mb-4">
                            <FormInput
                                label="Name"
                                placeholder="Backtest Name"
                                value={backtestName}
                                onChange={(e) => setBacktestName(e.target.value)}
                            />
                        </div>

                        {!portfolioId && (
                            <div className="mb-4">
                                <PortfolioSelect
                                    label="Portfolio"
                                    placeholder="Select a portfolio"
                                    value={selectedPortfolioId ?? null}
                                    onChange={(value) => {
                                        setDuplicateSourceBacktest(null);
                                        setSelectedPortfolioId(value ?? undefined);
                                    }}
                                />
                            </div>
                        )}

                        <FormNumberInput
                            helpText="Cash available at the start of this backtest. Defaults to $100,000."
                            label="Starting capital"
                            min={0.01}
                            prefix="$"
                            step={1000}
                            value={startingBalance}
                            onValueChange={setStartingBalance}
                        />

                        <div className="mb-4">
                            <FormCheckbox
                                label="Compare before and after taxes"
                                checked={taxesEnabled}
                                onChange={(event) => setTaxesEnabled(event.target.checked)}
                            />
                            {taxesEnabled && (
                                <div className="rounded border border-zinc-700 p-3 mb-4">
                                    <FormNumberInput
                                        label="Federal tax rate"
                                        suffix="%"
                                        min={0}
                                        max={100}
                                        step={0.1}
                                        value={federalTaxRate}
                                        onValueChange={setFederalTaxRate}
                                    />
                                    <FormNumberInput
                                        label="State tax rate"
                                        suffix="%"
                                        min={0}
                                        max={100}
                                        step={0.1}
                                        value={stateTaxRate}
                                        onValueChange={setStateTaxRate}
                                    />
                                    <p className="text-xs text-zinc-400">
                                        Flat-rate estimate treating capital gains as short-term, with annual loss carryforwards. The prior
                                        year’s bill is reserved from January 1 and paid from cash on April 15; insufficient cash fails the
                                        run. No wash-sale adjustments, outside income, or estimated quarterly payments. Futures, index
                                        options, and physical option exercise/assignment are unsupported.
                                    </p>
                                </div>
                            )}
                            <div className="block text-zinc-200 mb-2">Research window</div>
                            <div aria-label="Research window presets" className="flex flex-wrap gap-2" role="group">
                                <button
                                    className="rounded bg-zinc-700 px-3 py-1 text-sm text-zinc-100 hover:bg-zinc-600"
                                    type="button"
                                    onClick={() => applyResearchWindow('training')}
                                >
                                    Training (in-sample)
                                </button>
                                <button
                                    className="rounded bg-zinc-700 px-3 py-1 text-sm text-zinc-100 hover:bg-zinc-600"
                                    type="button"
                                    onClick={() => applyResearchWindow('walkforward')}
                                >
                                    Walk-forward (OOS)
                                </button>
                                <button
                                    className="rounded bg-zinc-700 px-3 py-1 text-sm text-zinc-100 hover:bg-zinc-600"
                                    type="button"
                                    onClick={() => applyResearchWindow('full')}
                                >
                                    Full range
                                </button>
                            </div>
                        </div>

                        <div className="mb-4">
                            <label className="block text-zinc-200 mb-2" htmlFor="startDate">
                                Start Date
                            </label>
                            <div className="flex space-x-2">
                                <FormDateInput
                                    id="startDate"
                                    value={startDate.slice(0, 10)}
                                    onChange={(e) => {
                                        // A cleared input emits '' — keep the last valid value (controlled input snaps back).
                                        if (e.target.value) setStartDate(`${e.target.value} ${startDate.slice(11)}`);
                                    }}
                                />
                                <FormTimeInput
                                    id="startTime"
                                    value={startDate.slice(11, 16)}
                                    onChange={(e) => {
                                        if (e.target.value) setStartDate(`${startDate.slice(0, 10)} ${e.target.value}:00`);
                                    }}
                                />
                            </div>
                        </div>

                        <div className="mb-4">
                            <label className="block text-zinc-200 mb-2" htmlFor="endDate">
                                End Date
                            </label>
                            <div className="flex space-x-2">
                                <FormDateInput
                                    id="endDate"
                                    value={endDate.slice(0, 10)}
                                    onChange={(e) => {
                                        // A cleared input emits '' — keep the last valid value (controlled input snaps back).
                                        if (e.target.value) setEndDate(`${e.target.value} ${endDate.slice(11)}`);
                                    }}
                                />
                                <FormTimeInput
                                    id="endTime"
                                    value={endDate.slice(11, 16)}
                                    onChange={(e) => {
                                        if (e.target.value) setEndDate(`${endDate.slice(0, 10)} ${e.target.value}:00`);
                                    }}
                                />
                            </div>
                        </div>

                        <div className="mb-4">
                            <label className="block text-zinc-200 mb-2" htmlFor="trainingEndDate">
                                Walk-forward Start (Eastern)
                            </label>
                            <div className="flex space-x-2">
                                <FormDateInput
                                    id="trainingEndDate"
                                    value={trainingEndDate.slice(0, 10)}
                                    onChange={(e) => {
                                        if (e.target.value) setTrainingEndDate(`${e.target.value} ${trainingEndDate.slice(11)}`);
                                    }}
                                />
                                <FormTimeInput
                                    id="trainingEndTime"
                                    aria-label="Walk-forward start time (Eastern)"
                                    value={trainingEndDate.slice(11, 16)}
                                    onChange={(e) => {
                                        if (e.target.value) setTrainingEndDate(`${trainingEndDate.slice(0, 10)} ${e.target.value}:00`);
                                    }}
                                />
                            </div>
                            <p className="mt-2 text-xs text-zinc-400">
                                Saved with this run. Earlier entries are training; entries at or after this time are walk-forward. Use the
                                experiment&apos;s original cutoff for a matched comparison. The End Date limits entries, not later exits.
                            </p>
                        </div>

                        {isFuturesSelection && (
                            <Card
                                title="Futures products and assumptions"
                                subtitle="Intraday research only. Portfolios retain the 100,000-minute window and eight strategy/product limit; multi-year runs are available for individual strategies."
                            >
                                <div className="space-y-4">
                                    {futuresDrafts.map((draft, index) => (
                                        <div key={index}>
                                            <FuturesConfigurationFields
                                                strategyIds={(modalPortfolio?.strategiesSubscribed ?? [])
                                                    .filter(
                                                        (strategy) =>
                                                            includedStrategyIds.has(strategy.id) &&
                                                            strategy.allowedUnderlyingEntityTypes.includes('Asset') &&
                                                            strategy.legs.some((leg) => leg.derivativeType === 'Future')
                                                    )
                                                    .map((strategy) => strategy.id)}
                                                value={draft}
                                                onChange={(value) =>
                                                    setFuturesDrafts((current) => current.map((item, i) => (i === index ? value : item)))
                                                }
                                            />
                                            {futuresDrafts.length > 1 && (
                                                <Button
                                                    variant="outline-dark"
                                                    onClick={() => setFuturesDrafts((current) => current.filter((_, i) => i !== index))}
                                                >
                                                    Remove product
                                                </Button>
                                            )}
                                        </div>
                                    ))}
                                    <Button
                                        disabled={futuresDrafts.length >= 8}
                                        variant="outline-dark"
                                        onClick={() => setFuturesDrafts((current) => [...current, emptyFuturesConfiguration()])}
                                    >
                                        Add product
                                    </Button>
                                </div>
                            </Card>
                        )}

                        <PortfolioBacktestCapitalControls
                            directionMaxRules={backtestDirectionMaxRules}
                            portfolioDirectionMaxRules={modalPortfolio?.settings?.directionMaxRules ?? []}
                            portfolioPositionSizeRules={modalPortfolio?.settings?.positionSizeRules ?? []}
                            positionSizeRules={backtestPositionSizeRules}
                            onDirectionMaxRulesChange={setBacktestDirectionMaxRules}
                            onPositionSizeRulesChange={setBacktestPositionSizeRules}
                        />

                        <hr className="border-zinc-600 my-4" />

                        <h5 className="text-lg font-medium text-white mb-4">Advanced Options:</h5>

                        <div className="mb-4">
                            <FormCheckbox
                                checked={shouldLog}
                                helpText="Creates detailed transaction logs for debugging purposes"
                                id="shouldLog"
                                label="Enable detailed transaction logging"
                                onChange={(e) => setShouldLog(e.target.checked)}
                            />
                        </div>

                        <div className="mb-4">
                            <FormNumberInput
                                integer
                                helpText="Higher numbers = higher priority. Priority 10 processes first, 1 processes last. Default is 8."
                                label="Orchestrator Priority"
                                max={10}
                                min={1}
                                placeholder="Priority (1-10)"
                                value={orchestratorPriority}
                                onValueChange={(value) => {
                                    // The backtest always carries a priority, so an empty field keeps the current one.
                                    if (value === null) return;

                                    setOrchestratorPriority(value);
                                }}
                            />
                        </div>

                        <div className="mb-4">
                            <FormCheckbox
                                checked={bypassCache}
                                helpText="Bypasses the entry cache and forces fresh calculation of all strategy entries"
                                id="bypassCache"
                                label="Bypass entry cache"
                                onChange={(e) => setBypassCache(e.target.checked)}
                            />
                        </div>

                        <hr className="border-zinc-600 my-4" />
                        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <h5 className="text-lg font-medium text-white">Strategy Settings:</h5>
                            <div className="flex flex-wrap items-center gap-2 text-sm">
                                <span className="text-zinc-300">
                                    {includedStrategyIds.size}/{modalPortfolio?.strategiesSubscribed?.length ?? 0} selected
                                </span>
                                <button
                                    className="rounded bg-zinc-700 px-3 py-1 text-zinc-100 hover:bg-zinc-600"
                                    type="button"
                                    onClick={includeAllSubscribedStrategies}
                                >
                                    All
                                </button>
                                <button
                                    className="rounded bg-zinc-700 px-3 py-1 text-zinc-100 hover:bg-zinc-600"
                                    type="button"
                                    onClick={includeActiveSubscribedStrategies}
                                >
                                    Active only
                                </button>
                            </div>
                        </div>

                        {tradingProfilesLoading ? (
                            <div className="py-6">
                                <DataLoadingSpinner text="Loading trading profiles..." />
                            </div>
                        ) : (
                            modalPortfolio?.strategiesSubscribed?.map((strategy) => {
                                const isIncluded = includedStrategyIds.has(strategy.id);
                                const strategySetting = strategySettings[strategy.id];
                                const preflight = exitCandidatePreflightByStrategy.get(strategy.id);
                                const isOptionsStrategy = preflight?.isOptionsStrategy ?? strategyUsesOptions(strategy);
                                const optionBuyingStrategies = preflight?.optionBuyingStrategies ?? [];
                                const hasOptionBuyingStrategyPreflight =
                                    !isOptionsStrategy ||
                                    (preflight?.optionBuyingStrategies !== undefined &&
                                        preflight.selectedOptionBuyingStrategyIds !== undefined);
                                const effectiveOptionBuyingStrategyIds = optionBuyingStrategies.map(
                                    (optionBuyingStrategy) => optionBuyingStrategy.id
                                );
                                const deploymentOptionBuyingStrategyIds = strategy.optionBuyingStrategyIds ?? [];
                                const selectedOptionBuyingStrategyIds = strategySetting?.optionBuyingStrategyIds ?? [];
                                const hasOptionBuyingStrategyOverride =
                                    selectedOptionBuyingStrategyIds.length !== deploymentOptionBuyingStrategyIds.length ||
                                    selectedOptionBuyingStrategyIds.some(
                                        (optionBuyingStrategyId) => !deploymentOptionBuyingStrategyIds.includes(optionBuyingStrategyId)
                                    );
                                const effectiveCandidateIds = preflight?.candidates.map((candidate) => candidate.id) ?? [];
                                const deploymentExitCandidateIds = strategy.exitCandidateIds ?? [];
                                const selectedExitCandidateIds = strategySetting?.exitCandidateIds ?? [];
                                const hasExitCandidateOverride =
                                    selectedExitCandidateIds.length !== deploymentExitCandidateIds.length ||
                                    selectedExitCandidateIds.some(
                                        (exitCandidateId) => !deploymentExitCandidateIds.includes(exitCandidateId)
                                    );

                                return (
                                    <div className={`mb-6 bg-zinc-750 p-4 rounded-md ${isIncluded ? '' : 'opacity-60'}`} key={strategy.id}>
                                        <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <h6 className="text-white font-medium">{strategy.name}</h6>
                                                <span className="font-mono text-xs text-zinc-400">ID: {strategy.id}</span>
                                                <span
                                                    className={`rounded px-2 py-0.5 text-xs ${
                                                        strategy.isActive ? 'bg-emerald-900 text-emerald-200' : 'bg-zinc-700 text-zinc-300'
                                                    }`}
                                                >
                                                    {strategy.isActive ? 'Active' : 'Inactive'}
                                                </span>
                                            </div>
                                            <FormCheckbox
                                                aria-label={`Include ${strategy.name}`}
                                                checked={isIncluded}
                                                id={`include-strategy-${strategy.id}`}
                                                label="Include"
                                                onChange={(e) => handleStrategyIncludedChange(strategy.id, e.target.checked)}
                                            />
                                        </div>

                                        <div className="mb-3">
                                            <TradingProfileSelect
                                                isDisabled={!isIncluded}
                                                label="Settings"
                                                placeholder="Choose settings"
                                                value={strategySettings[strategy.id]?.settingsId ?? null}
                                                onChange={(value) => handleStrategySettingChange(strategy.id, value)}
                                            />
                                        </div>

                                        {isIncluded && strategySetting?.settingsId && (
                                            <div className="rounded border border-zinc-700 bg-zinc-900/50 p-3">
                                                {isOptionsStrategy && preflight && !hasOptionBuyingStrategyPreflight ? (
                                                    <Alert variant="danger">
                                                        OBS selection data is unavailable. Refresh after the backend deployment is complete.
                                                    </Alert>
                                                ) : isOptionsStrategy && preflight ? (
                                                    <div className="mb-5 border-b border-zinc-700 pb-5">
                                                        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                                                            <div>
                                                                <div className="text-sm font-medium text-zinc-100">
                                                                    Option buying strategies
                                                                </div>
                                                                <div className="text-xs text-zinc-400">
                                                                    Starts with this portfolio strategy&apos;s live deployment. This run
                                                                    freezes its own explicit selection without changing live trading.
                                                                </div>
                                                            </div>
                                                            {hasOptionBuyingStrategyOverride && (
                                                                <button
                                                                    className="rounded bg-zinc-700 px-3 py-1 text-xs text-zinc-100 hover:bg-zinc-600"
                                                                    type="button"
                                                                    onClick={() =>
                                                                        setStrategySettings((prev) => {
                                                                            const current = prev[strategy.id];
                                                                            if (!current) return prev;
                                                                            return {
                                                                                ...prev,
                                                                                [strategy.id]: {
                                                                                    ...current,
                                                                                    optionBuyingStrategyIds: [
                                                                                        ...deploymentOptionBuyingStrategyIds
                                                                                    ]
                                                                                }
                                                                            };
                                                                        })
                                                                    }
                                                                >
                                                                    Use live deployment
                                                                </button>
                                                            )}
                                                        </div>

                                                        <OptionBuyingStrategySelect
                                                            allowedIds={effectiveOptionBuyingStrategyIds}
                                                            isDisabled={!isIncluded}
                                                            label="OBS for this backtest"
                                                            mode="multi"
                                                            noOptionsMessage="No active option buying strategies are available for this strategy"
                                                            placeholder="Choose one or more option buying strategies"
                                                            value={selectedOptionBuyingStrategyIds}
                                                            onChange={(optionBuyingStrategyIds) => {
                                                                if (optionBuyingStrategyIds.length === 0) {
                                                                    showToast(
                                                                        'Select at least one option buying strategy for this backtest',
                                                                        'error'
                                                                    );
                                                                    return;
                                                                }

                                                                setStrategySettings((prev) => {
                                                                    const current = prev[strategy.id];
                                                                    if (!current) return prev;
                                                                    return {
                                                                        ...prev,
                                                                        [strategy.id]: {
                                                                            ...current,
                                                                            optionBuyingStrategyIds
                                                                        }
                                                                    };
                                                                });
                                                            }}
                                                        />
                                                    </div>
                                                ) : null}

                                                <div className="mb-5 border-b border-zinc-700 pb-5">
                                                    <StrategyPositionSizeRules
                                                        defaultRules={backtestPositionSizeRules}
                                                        rules={strategySetting.positionSizeRules}
                                                        onChange={(positionSizeRules) =>
                                                            setStrategySettings((prev) => {
                                                                const current = prev[strategy.id];
                                                                if (!current) return prev;

                                                                if (positionSizeRules === undefined) {
                                                                    const inheritedSettings = { ...current };
                                                                    delete inheritedSettings.positionSizeRules;
                                                                    return { ...prev, [strategy.id]: inheritedSettings };
                                                                }

                                                                return {
                                                                    ...prev,
                                                                    [strategy.id]: {
                                                                        ...current,
                                                                        positionSizeRules
                                                                    }
                                                                };
                                                            })
                                                        }
                                                    />
                                                </div>

                                                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                                                    <div>
                                                        <div className="text-sm font-medium text-zinc-100">Exit candidates</div>
                                                        <div className="text-xs text-zinc-400">
                                                            Starts with this portfolio strategy&apos;s live deployment. This run freezes its
                                                            own explicit selection without changing live trading.
                                                        </div>
                                                    </div>
                                                    {hasExitCandidateOverride && (
                                                        <button
                                                            className="rounded bg-zinc-700 px-3 py-1 text-xs text-zinc-100 hover:bg-zinc-600"
                                                            type="button"
                                                            onClick={() =>
                                                                setStrategySettings((prev) => {
                                                                    const current = prev[strategy.id];
                                                                    if (!current) return prev;
                                                                    return {
                                                                        ...prev,
                                                                        [strategy.id]: {
                                                                            ...current,
                                                                            exitCandidateIds: [...deploymentExitCandidateIds]
                                                                        }
                                                                    };
                                                                })
                                                            }
                                                        >
                                                            Use live deployment
                                                        </button>
                                                    )}
                                                </div>

                                                {exitCandidatePreflightQuery.isLoading && !preflight ? (
                                                    <DataLoadingSpinner text="Validating backtest selections..." />
                                                ) : preflight ? (
                                                    <>
                                                        <ExitCandidateSelect
                                                            allowedIds={effectiveCandidateIds}
                                                            isDisabled={!isIncluded}
                                                            label="Candidates for this backtest"
                                                            mode="multi"
                                                            noOptionsMessage="No effective exit candidates are available"
                                                            placeholder="Choose one or more candidates"
                                                            value={selectedExitCandidateIds}
                                                            onChange={(candidateIds) => {
                                                                if (candidateIds.length === 0) {
                                                                    showToast(
                                                                        'Select at least one exit candidate for this backtest',
                                                                        'error'
                                                                    );
                                                                    return;
                                                                }

                                                                setStrategySettings((prev) => {
                                                                    const current = prev[strategy.id];
                                                                    if (!current) return prev;
                                                                    return {
                                                                        ...prev,
                                                                        [strategy.id]: {
                                                                            ...current,
                                                                            exitCandidateIds: candidateIds
                                                                        }
                                                                    };
                                                                });
                                                            }}
                                                        />
                                                        <div className="mt-2 flex flex-wrap gap-2 text-xs text-zinc-400">
                                                            {preflight.candidates.map((candidate) => (
                                                                <span
                                                                    className={`rounded px-2 py-1 ${
                                                                        candidate.isDefault
                                                                            ? 'bg-blue-950 text-blue-200'
                                                                            : candidate.isBase
                                                                              ? 'bg-emerald-950 text-emerald-200'
                                                                              : 'bg-zinc-800 text-zinc-300'
                                                                    }`}
                                                                    key={candidate.id}
                                                                >
                                                                    {candidate.label} · #{candidate.id}
                                                                    {candidate.isDefault ? ' · default' : candidate.isBase ? ' · base' : ''}
                                                                </span>
                                                            ))}
                                                        </div>
                                                        <div className="mt-3 text-xs text-zinc-300">
                                                            {strategy.allowedUnderlyingEntityTypes.includes('FuturesProduct') ||
                                                            strategy.legs.some((leg) => leg.derivativeType === 'Future') ? (
                                                                <>Futures coverage is verified from the selected products when queued.</>
                                                            ) : preflight.coverage.requestId === null ? (
                                                                <>
                                                                    Coverage preview: {preflight.coverage.expectedBacktests} backtests
                                                                    required · launch will reuse cached backtests and queue only missing
                                                                    work
                                                                </>
                                                            ) : (
                                                                <>
                                                                    Backtest coverage: {preflight.coverage.readyBacktests}/
                                                                    {preflight.coverage.expectedBacktests} ready
                                                                    {preflight.coverage.missingBacktests > 0
                                                                        ? ` · ${preflight.coverage.missingBacktests} missing (will be queued)`
                                                                        : ''}
                                                                    {preflight.coverage.pendingBacktests > 0
                                                                        ? ` · ${preflight.coverage.pendingBacktests} pending`
                                                                        : ''}
                                                                    {preflight.coverage.failedBacktests > 0
                                                                        ? ` · ${preflight.coverage.failedBacktests} failed`
                                                                        : ''}
                                                                </>
                                                            )}
                                                        </div>
                                                        {(preflight.coverage.errorMessages.length > 0 ||
                                                            preflight.coverage.failedBacktests > 0) && (
                                                            <Alert variant="danger">
                                                                {preflight.coverage.errorMessages.join(' ') ||
                                                                    'One or more required strategy backtests failed.'}
                                                            </Alert>
                                                        )}
                                                    </>
                                                ) : exitCandidatePreflightQuery.error ? (
                                                    <Alert variant="danger">
                                                        Could not validate backtest selections for this strategy.
                                                    </Alert>
                                                ) : null}
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        )}

                        {usesCohortBestBacktestSelection && (
                            <PortfolioBacktestEvidenceModeControl
                                affectedStrategyNames={cohortSelectionStrategyNames}
                                hasAcknowledgedReconstruction={hasAcknowledgedBacktestReconstruction}
                                mode={backtestSelectionArtifactUniverseMode}
                                onAcknowledgementChange={setHasAcknowledgedBacktestReconstruction}
                                onModeChange={(mode) => {
                                    setBacktestSelectionArtifactUniverseMode(mode);

                                    if (mode === 'strict_operational') {
                                        setHasAcknowledgedBacktestReconstruction(false);
                                    }
                                }}
                            />
                        )}
                    </div>
                </Modal>

                {/* Settings Modal */}
                <Modal isOpen={showSettingsModal} size="lg" title="Trading Profile" onClose={() => setShowSettingsModal(false)}>
                    <div className="p-4 rounded-md">{currentSettings && <TradingProfileDisplay settings={currentSettings} />}</div>
                </Modal>

                <ConfirmationDialog
                    confirmText={pendingDeleteIds.length === 1 ? 'Delete Backtest' : 'Delete Backtests'}
                    confirmVariant="danger"
                    isConfirmLoading={deleteBacktestsMutation.isPending}
                    isOpen={pendingDeleteIds.length > 0}
                    message={
                        pendingDeleteIds.length === 1
                            ? `Are you sure you want to delete ${pendingDeleteLabel}? This action cannot be undone.`
                            : `Are you sure you want to delete ${pendingDeleteIds.length.toLocaleString()} backtests? This action cannot be undone.`
                    }
                    title="Confirm Deletion"
                    onCancel={cancelDeleteBacktests}
                    onConfirm={confirmDeleteBacktests}
                />
            </div>
        </UserLayout>
    );
};

export default PortfolioBacktestComponent;
